import { Box, Button, Paper, Stack, styled } from '@mui/material';
import PageHeader from '../../controls/PageHeader';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import TextSearch from '../../controls/TextSearch';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { DataGridPro } from '../../tables/DataGridPro';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import * as clientSlice from '../../../data/client.slice';
import { ZipCodeRange } from '../../../data/models/service-plan.model';
import ServiceAreaDialog from './ServiceAreaDialog';

const PREFIX = 'ClientServiceAreas';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  contentContainer: `${PREFIX}-contentContainer`,
  search: `${PREFIX}-search`,
  addButton: `${PREFIX}-addButton`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(1),
  },
  [`& .${classes.contentContainer}`]: {
    padding: theme.spacing(2),
  },
  [`& .${classes.header}`]: {
    justifyContent: 'space-between',
  },
  [`& .${classes.search}`]: {
    width: 320,
    marginRight: theme.spacing(3),
  },
  [`& .${classes.addButton}`]: {
    width: 150,
  },
}));

export default function ClientServiceAreas() {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();

  const client = useAppSelector(clientSlice.selectCurrentClient);
  const serviceAreas = useAppSelector(clientSlice.selectServiceAreas);
  const loading = useAppSelector(clientSlice.selectLoadingServiceAreas);

  const [searchText, setSearchText] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (client) {
      dispatch(clientSlice.fetchClientServiceAreasAsync(client.clientBC));
    }
  }, [client]);

  const filteredServiceAreas = useMemo(() => {
    if (searchText) {
      return serviceAreas.filter((sa) => {
        const lcName = sa.name.toLowerCase();
        const lcSearchText = searchText.toLowerCase();
        const foundZipOrState = sa.zipCodes.reduce(
          (prev: boolean, cur: ZipCodeRange) => {
            return (
              prev ||
              cur.stateCode.toLocaleLowerCase().indexOf(lcSearchText) >= 0 ||
              (cur.zipCode ?? '').indexOf(lcSearchText) >= 0 ||
              (cur.zipCodeBegin ?? '').indexOf(lcSearchText) >= 0 ||
              (cur.zipCodeEnd ?? '').indexOf(lcSearchText) >= 0
            );
          },
          false,
        );
        return lcName.indexOf(lcSearchText) >= 0 || foundZipOrState;
      });
    }
    return serviceAreas;
  }, [searchText, serviceAreas]);

  const handleSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const renderZipCodes = (params: GridRenderCellParams) => {
    const zips = params.value;
    const zipStr = zips.reduce((prev: string, current: ZipCodeRange) => {
      if (current.zipCode) {
        return prev ? `${prev}, ${current.zipCode}` : `${current.zipCode}`;
      } else if (current.zipCodeBegin && current.zipCodeEnd) {
        const range = `${current.zipCodeBegin}-${current.zipCodeEnd}`;
        return prev ? `${prev}, ${range}` : `${range}`;
      }
      return prev;
    }, '');

    return <>{zipStr}</>;
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('client.serviceAreaName'),
      sortable: false,
      minWidth: 200,
    },
    {
      field: 'zipCodes',
      headerName: t('client.zipCodes'),
      sortable: false,
      flex: 1,
      renderCell: renderZipCodes,
    },
  ];

  const handleAddServiceArea = () => {
    setDialogOpen(true);
  };

  return (
    <StyledPaper>
      <Box className={classes.container}>
        <PageHeader
          title={t('client.serviceAreas')}
          returnLink={`/clients/${client?.clientBC}`}
          returnLinkText={client?.legalName}
        />
        <Stack direction="column" className={classes.contentContainer}>
          <Stack direction="row" className={classes.header}>
            <Stack direction="row">
              <TextSearch
                id="text-search"
                className={classes.search}
                icon={<SearchIcon />}
                label={t('client.search')}
                currentSearchFilter={searchText}
                displayClearButton={true}
                onSearchChange={handleSearchTextChange}
              />
            </Stack>
            <Button
              className={classes.addButton}
              color="primary"
              variant="contained"
              onClick={handleAddServiceArea}>
              {t('common.add')}
            </Button>
          </Stack>
          <DataGridPro
            disableColumnMenu={true}
            getRowId={(row) => row.serviceAreaUUID}
            rows={filteredServiceAreas}
            columns={columns}
            pagination={false}
            autoHeight
            loading={loading}
            hideFooter={true}
            rowSelection={false}
          />
        </Stack>
      </Box>
      {client && (
        <ServiceAreaDialog
          title={t('client.addServiceArea')}
          clientBC={client.clientBC}
          open={dialogOpen}
          close={() => setDialogOpen(false)}
        />
      )}
    </StyledPaper>
  );
}
